import { productContext } from "@/contexts/context.product";
import { ActionEvent } from "@/events/events";
import { toDSLContext } from "@/renders/context";
import { provide } from "@lit/context";
import { Task } from "@lit/task";
import type { Product } from "@repobit/dex-store";
import { property } from "lit/decorators.js";
import { StateNode } from "./node.state";

export class ProductNode extends StateNode {
  // Use custom forward tasks; disable default passthrough.
  autoForward = false;
  @property({ attribute: 'product-id', reflect: true })
  productId?: string;

  @property({ reflect: true })
  campaign?: string;

  @provide({ context: productContext })
  @property({ attribute: false })
  product?: Product;

  private _forwardAndNotify(event?: ActionEvent) {
    this._forwardEvent(event);
    this._notifyParent();
  }

  // Reflect incoming product-change events and forward original event.
  private _productChangeEvent = new Task(this, {
    task: ([evt]) => {
      if (!evt) return null;
      if (this.isProductChange(evt)) {
        this.productId = evt.detail.id;
        this.campaign = evt.detail.campaign;
      }
      this._forwardAndNotify(evt);
    },
    args: () => [this._event]
  });

  // Synthesize product-change events when attributes change locally.
  private _forwardEventTask = new Task(this, {
    task: ([id, campaign]) => {
      const evt = this._event;
      if (evt && this.isProductChange(evt)) {
        const { id: evtId, campaign: evtCampaign } = evt.detail;
        if (evtId === id && evtCampaign === campaign) {
          return; // skip resynthesizing if mirroring incoming event
        }
      }
      if (id) {
        this._forwardAndNotify(new ActionEvent({ id, campaign }));
      }
    },
    args: () => [this.productId, this.campaign]
  });

  private _loadProductTask = new Task(this, {
    task: async ([store, id, campaign]) => {
      if (!store || !id) {
        this.product = undefined;
        return undefined;
      }
      const product = await store.getProduct({ id, campaign });
      this.product = product;
      return product;
    },
    args: () => [this._store, this.productId, this.campaign]
  });

  override getEtaArgs(): unknown[] {
    return [this.product, this.state, this._derived];
  }

  override async buildEtaContext(): Promise<object | undefined> {
    if (!this.product) return undefined;
    return await toDSLContext({ product: this.product, state: this.state, derived: this._derived });
  }

  protected async getUpdateComplete() {
    const result = await super.getUpdateComplete();
    await this._productChangeEvent.taskComplete;
    await this._forwardEventTask.taskComplete;
    await this._loadProductTask.taskComplete;
    return result;
  }
}
